/**
 * @module api/transcript
 *
 * API functions for on-demand episode transcripts (captions fetched via
 * yt-dlp on the server, cleaned into plain text, and cached there — see
 * server/src/services/transcript.js).
 *
 * All functions communicate with the server via the shared Axios instance
 * from config.js and follow the standard response envelope
 * { success, data | error }.
 */

import { api } from "./config.js";

/**
 * Extracts the most useful error message from an Axios error, preferring the
 * server's structured error/message fields over the generic fallback.
 *
 * @param {*} error - The caught Axios error
 * @param {string} fallback - Message to use when the server gave none
 * @returns {Error} A plain Error carrying the resolved message and HTTP status
 */
const toError = (error, fallback) => {
  const message =
    error.response?.data?.error ||
    error.response?.data?.message ||
    fallback;
  const err = new Error(message);
  err.status = error.response?.status;
  return err;
};

/**
 * Fetch (or trigger a server-side fetch of) an episode's transcript. Can take
 * a few seconds on a cache miss — the server runs yt-dlp synchronously before
 * responding — so callers should show a loading state while this resolves.
 *
 * The response is NOT an error just because the video has no captions: check
 * `data.available` — when false, `data.reason` has a human-readable message
 * ("No captions available for this video", ...).
 *
 * @param {string} videoId - The episode's video_id
 * @param {Object} [opts]
 * @param {string} [opts.language] - Force this exact language code (e.g.
 *   "it"), REPLACING whatever's cached, instead of trusting auto-detection.
 *   The manual correction for when YouTube's own metadata language is
 *   simply wrong (confirmed on a real episode — see CLAUDE.md's transcript
 *   section). Also clears any cached AI summary server-side, since it would
 *   have been generated from the old, wrong-language transcript.
 * @returns {Promise<{available: boolean, text?: string, language?: string, cached?: boolean, reason?: string}>}
 * @throws {Error} Only on an actual request failure (network/server error)
 */
const getTranscript = async (videoId, { language } = {}) => {
  try {
    const response = await api.get(`/episode/transcript/${encodeURIComponent(videoId)}`, {
      params: language ? { lang: language } : undefined,
    });
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to load transcript");
  }
};

/**
 * Summarize an episode's transcript via the server's AI summarization
 * (Groq — see server/src/services/summarize.js). Ensures the transcript
 * itself has been fetched first, so this can be called even before a
 * getTranscript() call — but callers normally only offer it once a
 * transcript is already showing.
 *
 * Same non-error convention as getTranscript: check `data.available` —
 * false can mean no transcript to summarize, or the server has no
 * GROQ_API_KEY configured; `data.reason` has the human-readable detail.
 *
 * @param {string} videoId - The episode's video_id
 * @returns {Promise<{available: boolean, summary?: string, cached?: boolean, reason?: string}>}
 * @throws {Error} Only on an actual request failure (network/server error)
 */
const summarizeTranscript = async (videoId) => {
  try {
    // Longer timeout than the shared client's default (30s, api/config.js):
    // on a cache miss this chains TWO server-side network calls — fetching
    // the transcript, then the Groq call — where the transcript fetch alone
    // can take up to ~15s. Measured against production: a genuinely uncached
    // old episode took 14.3s end-to-end; 60s leaves real headroom instead of
    // cutting it close on a slower one.
    const response = await api.post(
      `/episode/transcript/${encodeURIComponent(videoId)}/summarize`,
      {},
      { timeout: 60000 },
    );
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to summarize transcript");
  }
};

export { getTranscript, summarizeTranscript };
